import { siopColors } from '../../config/theme';

// Shared visual language for the SIOP cockpit's data tables, matching the
// reference: a flat WHITE body with real grid lines (no per-column pastel
// tinting — that's what was making everything blend together), a clearly grey
// header band (not a near-white one), and colour reserved strictly for a
// genuinely flagged cell — never decoration.

// ── Surface language ────────────────────────────────────────────────────────
// One border colour, one radius and one shadow for every raised surface in the
// cockpit. Before this, four different components each invented their own
// (16px/#D1D5DB tables, 12px/#E4E4E4 KPI cards, MUI's 4px/divider outlined
// Paper for chart cards, 16px/#E4E4E4 filters), so panels sitting side by side
// on the same screen had visibly different corners and edge colours.
//
// These are chrome-only tokens: radius, border, shadow, hover. Nothing here
// changes a size, a spacing value or a layout rule.
export const SURFACE_BORDER = '#E3E7EE';
export const SURFACE_RADIUS = '14px';
// A single hairline shadow rather than MUI's stacked elevation — enough to lift
// a white panel off the near-white page background without the 2010s drop
// shadow that "elevation 1" still renders.
export const SURFACE_SHADOW = '0 1px 2px rgba(16, 24, 40, 0.04)';

export const CARD_SX = {
  overflow: 'hidden',
  borderRadius: SURFACE_RADIUS,
  border: `1px solid ${SURFACE_BORDER}`,
  boxShadow: SURFACE_SHADOW,
};

// A padded panel that is not a table — chart cards, filter bars, KPI tiles.
// Same edge as CARD_SX; the caller still owns its own padding and height.
export const PANEL_SX = {
  borderRadius: SURFACE_RADIUS,
  border: `1px solid ${SURFACE_BORDER}`,
  boxShadow: SURFACE_SHADOW,
  bgcolor: '#fff',
};

// Interactive lift for a tile the eye is meant to scan (KPI cards). Transition
// only — the border darkens, nothing moves, so a hover cannot reflow the row.
export const PANEL_HOVER_SX = {
  transition: 'border-color 160ms ease, box-shadow 160ms ease',
  '&:hover': {
    borderColor: '#CBD2DD',
    boxShadow: '0 2px 8px rgba(16, 24, 40, 0.06)',
  },
};

// The small uppercase label above a KPI value / chart title. Was declared
// inline in three places with three different sizes and letter-spacings.
export const PANEL_LABEL_SX = {
  fontSize: 12,
  fontWeight: 700,
  color: '#6B7280',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};

// Lining figures for every money column: digits share one advance width, so
// numbers in a column align on their digits instead of drifting by glyph. The
// single highest-value typographic fix available to a financial table, and it
// changes no box dimensions.
export const NUMERIC_SX = { fontVariantNumeric: 'tabular-nums' };

// Vertical grid line. Deliberately faint: the columns are separated by the
// header rule and by tabular figures that already line up: a full-strength line
// between every pair of cells was drawing a cage around the data. Strong enough
// to follow a column across a wide pivot, quiet enough to disappear when you
// are reading a row.
const COL_DIVIDER = '1px solid #EEF1F5';

// Horizontal rule between body rows — the separator that actually does the work
// now that the verticals have stepped back.
const ROW_DIVIDER = '1px solid #EDF0F4';

// The header band. Was a solid #D9D9D9 slab, which is what made these tables
// read as a 2005 spreadsheet export: a mid-grey fill that heavy competes with
// the data for attention and leaves the head looking bolted on. Now a soft
// neutral tint closed with one confident rule underneath — the band is defined
// by its edge, not by its weight, and the labels carry the emphasis via case
// and tracking rather than a background.
// Exported because a sticky first header cell has to repaint the band behind
// itself as it scrolls over the body — it cannot inherit HEADER_ROW_SX.
export const HEADER_BG = '#F7F8FA';
const HEADER_RULE = '2px solid #DDE2EA';

// A top-level grouped header row (e.g. "2025" / "2026 Actuals / Forecast").
export const GROUP_HEADER_CELL_SX = {
  bgcolor: '#EFF2F6',
  color: '#111827',
  fontWeight: 700,
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: 0.7,
  borderBottom: '1px solid #DDE2EA',
  borderRight: COL_DIVIDER,
};

export const HEADER_ROW_SX = { bgcolor: HEADER_BG };

export const HEADER_CELL_SX = {
  color: '#4A5567',
  fontWeight: 700,
  fontSize: 11.5,
  textTransform: 'uppercase',
  letterSpacing: 0.7,
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  py: 1.25,
  borderBottom: HEADER_RULE,
  borderRight: COL_DIVIDER,
};

// A plain body cell — white bg (no pastel tint), faint dividers, and tabular
// figures so a money column lines up digit-for-digit.
export const BODY_CELL_SX = {
  borderRight: COL_DIVIDER,
  borderBottom: ROW_DIVIDER,
  fontSize: 13.5,
  color: '#1F2937',
  py: 1.05,
  ...NUMERIC_SX,
};

// Row hover for a table whose rows are scanned across (every data table) or
// clicked (Data Sources). One tint, defined once, so a hover never means two
// different things on two different screens.
export const ROW_HOVER_SX = {
  transition: 'background-color 120ms ease',
  '&:hover': { bgcolor: '#F7F9FC' },
};

// A group-summary row inside the body (e.g. a category row above its
// sub-brands) — a lighter, distinct tint from the header band (#D9D9D9), so
// it reads as "grouped section" rather than fusing into the header above it.
export const SUMMARY_ROW_SX = { bgcolor: '#F4F6F9' };

// Total / Grand Total row — plain white with bold text and a heavier top rule:
// colour is reserved for a genuinely flagged metric cell, not the row itself.
// The rule is a dark slate rather than pure black; at 2px, #111827 was the
// blackest thing on the page and pulled the eye before the numbers did.
export const TOTAL_ROW_SX = {
  bgcolor: '#fff',
  '& .MuiTableCell-root': {
    borderTop: '2px solid #98A2B3',
    borderBottom: 'none',
    borderRight: COL_DIVIDER,
  },
};

export const TOTAL_CELL_SX = { color: '#111827', fontWeight: 700, ...NUMERIC_SX };

// A neutral grey chip/badge — used instead of blue/green/navy-tinted chips so
// colour stays reserved for real data flags (gaps, thresholds), not decoration.
// Includes a visible border since the fill alone is too close to the white
// card background to read as a distinct chip.
export const NEUTRAL_CHIP_SX = {
  bgcolor: '#F3F4F6',
  color: '#1F2937',
  fontWeight: 600,
  border: '1px solid #E1E4E9',
};

// ── Status pill ─────────────────────────────────────────────────────────────
// A soft-tinted pill with a leading dot, replacing MUI's default filled/outlined
// Chip. The default renders a saturated block of colour that shouts louder than
// the row it annotates, and its outlined variant is a hollow ring that reads as
// a button. This is the shape every modern console uses for a status because it
// works: the dot carries the state at a glance, the tint keeps it legible, and
// nothing about it invites a click.
const STATUS_TONES = {
  neutral: { fg: '#4A5567', bg: '#F2F4F7', dot: '#98A2B3' },
  success: { fg: '#166534', bg: '#ECFDF3', dot: '#16A34A' },
  warning: { fg: '#92400E', bg: '#FFFAEB', dot: '#F59E0B' },
  info: { fg: '#1B4F8A', bg: '#EFF6FF', dot: '#2E75B6' },
};

export const statusPillSx = (tone = 'neutral') => {
  const t = STATUS_TONES[tone] || STATUS_TONES.neutral;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
    height: 24,
    px: 1.15,
    borderRadius: 999,
    bgcolor: t.bg,
    color: t.fg,
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    '&::before': {
      content: '""',
      width: 6,
      height: 6,
      borderRadius: '50%',
      bgcolor: t.dot,
      flexShrink: 0,
    },
  };
};

// A Total-row cell that breaches a threshold — solid fill, white bold text, the
// one place a summary row carries colour (mirrors the reference's flagged cell).
export const flagCellSx = (breached) =>
  breached
    ? { bgcolor: siopColors.gapFill, color: '#fff', fontWeight: 700 }
    : { color: '#111827', fontWeight: 700 };
